import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, exportAPI } from '../services/api';
import Layout from '../components/Layout';

// Simple SVG Icons
const Icons = {
  CurrencyDollar: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  ClipboardList: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
  ChartBar: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Building: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  Download: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
  Search: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Bell: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  DotsVertical: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
    </svg>
  )
};

const Reports = () => {
  const [stats, setStats] = useState({
    total_revenue: 0,
    total_cases: 0,
    avg_billing: 0,
    top_hospital: "Loading..."
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [hospitalData, setHospitalData] = useState([]);
  const [topMonths, setTopMonths] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState('12');

  useEffect(() => {
    fetchReportsData();
  }, [timeRange]); // Re-fetch when timeRange changes

  const fetchReportsData = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';
      const token = localStorage.getItem('token');
      
      // Fetch basic stats
      const statsData = await dashboardAPI.getStats();
      
      // Fetch services to calculate top hospital
      const servicesResp = await fetch(`${API_URL}/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let topHospitalName = "N/A";
      if (servicesResp.ok) {
        const services = await servicesResp.json();
        
        // Calculate earnings per hospital
        const hospitalEarnings = {};
        services.forEach(service => {
          const hospitalName = service.hospital_name || 'Unknown';
          if (!hospitalEarnings[hospitalName]) {
            hospitalEarnings[hospitalName] = 0;
          }
          hospitalEarnings[hospitalName] += parseFloat(service.amount_charged || 0);
        });
        
        // Find top earning hospital
        const topHospital = Object.entries(hospitalEarnings)
          .sort((a, b) => b[1] - a[1])[0];
        
        if (topHospital) {
          topHospitalName = topHospital[0];
        }
      }
      
      // Fetch all data in parallel
      const [monthlyResp, hospitalResp, topMonthsResp] = await Promise.all([
        fetch(`${API_URL}/dashboard/monthly-revenue`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_URL}/dashboard/hospital-performance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_URL}/dashboard/top-months`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null)
      ]);

      // Process monthly revenue
      if (monthlyResp && monthlyResp.ok) {
        const monthlyData = await monthlyResp.json();
        if (monthlyData && monthlyData.length > 0) {
          setMonthlyRevenue(monthlyData);
        }
      }

      // Process hospital performance
      if (hospitalResp && hospitalResp.ok) {
        const hospitalPerf = await hospitalResp.json();
        if (hospitalPerf && hospitalPerf.length > 0) {
          setHospitalData(hospitalPerf);
        }
      }

      // Process top months
      if (topMonthsResp && topMonthsResp.ok) {
        const topMonthsData = await topMonthsResp.json();
        if (topMonthsData && topMonthsData.length > 0) {
          setTopMonths(topMonthsData);
        }
      }

      setStats({
        total_revenue: statsData.total_revenue || 0,
        total_cases: statsData.total_services || 0,
        avg_billing: statsData.total_services > 0 ? (statsData.total_revenue / statsData.total_services) : 0,
        top_hospital: topHospitalName
      });
    } catch (error) {
      console.error('Error fetching reports data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportAPI.exportToExcel();
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Calculate donut chart percentages from real data
  const calculateHospitalPercentages = () => {
    if (hospitalData.length === 0) return [];
    
    const totalCases = hospitalData.reduce((sum, h) => sum + h.cases, 0);
    return hospitalData.map(h => ({
      ...h,
      percentage: totalCases > 0 ? ((h.cases / totalCases) * 100).toFixed(0) : 0
    }));
  };

  const hospitalPercentages = calculateHospitalPercentages();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const summaryCards = [
    {
      title: 'TOTAL REVENUE',
      value: `$${stats.total_revenue.toLocaleString()}`,
      icon: Icons.CurrencyDollar,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      title: 'TOTAL CASES',
      value: stats.total_cases,
      icon: Icons.ClipboardList,
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600'
    },
    {
      title: 'AVG. BILLING / CASE',
      value: `$${stats.avg_billing.toFixed(2)}`,
      badge: 'Avg',
      icon: Icons.ChartBar,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      title: 'TOP HOSPITAL',
      value: stats.top_hospital,
      badge: 'Top',
      icon: Icons.Building,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    }
  ];

  // Calculate total earnings across all months for percentage calculation
  const totalEarnings = monthlyRevenue.reduce((sum, m) => sum + m.value, 0);
  
  // Calculate max revenue from actual data for proper scaling
  const maxRevenue = monthlyRevenue.length > 0 ? Math.max(...monthlyRevenue.map(m => m.value), 1) : 1;
  
  // Generate Y-axis labels as percentages (0% to 100%)
  const generateYAxisLabels = () => {
    return [0, 25, 50, 75, 100]; // Percentage labels
  };
  
  const yAxisLabels = generateYAxisLabels();
  const yAxisMax = 100; // Always 100% for percentage-based scaling
  const hospitalColors = {
    blue: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-600' },
    cyan: { bg: 'bg-cyan-500', light: 'bg-cyan-100', text: 'text-cyan-600' },
    orange: { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-600' },
    green: { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-600' }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <Link to="/dashboard" className="hover:text-blue-600">Dashboard</Link>
              <span>›</span>
              <span className="text-gray-900 font-medium">Analytics</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Service Analytics & Reports</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Comprehensive view of your clinical earnings and performance.</p>
          </div>
          
          {/* Mobile-friendly controls */}
          <div className="flex flex-col gap-3">
            {/* Search and Refresh - Full width on mobile */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Icons.Search />
                </div>
                <input
                  type="text"
                  placeholder="Search data..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <button 
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                onClick={() => fetchReportsData()}
                title="Refresh data"
              >
                <Icons.Bell />
              </button>
            </div>
            
            {/* Time Range and Export - Stack on mobile */}
            <div className="flex flex-col sm:flex-row gap-2">
              <select 
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <option value="12">Last 12 Months</option>
                <option value="6">Last 6 Months</option>
                <option value="3">Last 3 Months</option>
              </select>
              <button 
                onClick={handleExport}
                disabled={exporting}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium text-sm disabled:opacity-50"
              >
                <Icons.Download />
                {exporting ? 'Exporting...' : 'Export Data'}
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {summaryCards.map((card, index) => (
            <div key={index} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className={`${card.iconBg} rounded-lg p-2 sm:p-3`}>
                  <div className={card.iconColor}>
                    <card.icon />
                  </div>
                </div>
                {card.badge && (
                  <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {card.badge}
                  </span>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  {card.title}
                </p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Monthly Revenue Trend */}
<div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
  <div className="flex items-center justify-between mb-6">
    <div>
      <h2 className="text-lg font-semibold text-gray-900">Monthly Revenue Trend</h2>
      <p className="text-sm text-gray-500">Revenue split over the last 12 months</p>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
      <span className="text-xs text-gray-600">Revenue</span>
    </div>
  </div>

  <div className="flex gap-4">
    {/* Y-axis */}
    <div className="flex flex-col justify-between h-64 py-2">
      {[...yAxisLabels].reverse().map((label, index) => (
        <span key={index} className="text-xs text-gray-500">
          {label}%
        </span>
      ))}
    </div>

    {/* Chart */}
    <div className="flex-1 flex items-end h-64 gap-3">
      {monthlyRevenue.length > 0 ? (
        monthlyRevenue.map((item, index) => {
          const percentage = totalEarnings > 0 ? (item.value / totalEarnings) * 100 : 0;
          const heightPercent = Math.max(percentage, item.value > 0 ? 3 : 0);

          return (
            <div key={index} className="flex-1 flex flex-col items-center gap-2 h-full">
              <div className="w-full flex-1 bg-gray-100 rounded-t-lg relative flex items-end">
                <div
                  className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all duration-500 hover:from-blue-700 hover:to-blue-500 cursor-pointer"
                  style={{ height: `${heightPercent}%` }}
                  title={`${item.month}: $${item.value.toLocaleString()} (${percentage.toFixed(1)}% of total)`}
                />
              </div>
              <span className="text-xs font-medium text-gray-600">
                {item.month}
              </span>
            </div>
          );
        })
      ) : (
        <div className="flex items-center justify-center w-full h-full text-gray-400">
          <p>No revenue data available</p>
        </div>
      )}
    </div>
  </div>
</div>


          {/* Hospital Split - Real Data Donut Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Hospital Split</h2>
            <p className="text-sm text-gray-500 mb-6">Service distribution per site</p>
            
            <div className="flex items-center justify-center mb-6">
              <div className="relative w-48 h-48">
                {/* SVG Donut Chart */}
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  {hospitalPercentages.length > 0 ? (
                    (() => {
                      let currentAngle = 0;
                      return hospitalPercentages.map((hospital, index) => {
                        const percentage = parseFloat(hospital.percentage);
                        const angle = (percentage / 100) * 360;
                        const startAngle = currentAngle;
                        currentAngle += angle;
                        
                        // Calculate arc path
                        const radius = 40;
                        const innerRadius = 28;
                        const centerX = 50;
                        const centerY = 50;
                        
                        const startRad = (startAngle * Math.PI) / 180;
                        const endRad = (currentAngle * Math.PI) / 180;
                        
                        const x1 = centerX + radius * Math.cos(startRad);
                        const y1 = centerY + radius * Math.sin(startRad);
                        const x2 = centerX + radius * Math.cos(endRad);
                        const y2 = centerY + radius * Math.sin(endRad);
                        
                        const x3 = centerX + innerRadius * Math.cos(endRad);
                        const y3 = centerY + innerRadius * Math.sin(endRad);
                        const x4 = centerX + innerRadius * Math.cos(startRad);
                        const y4 = centerY + innerRadius * Math.sin(startRad);
                        
                        const largeArc = angle > 180 ? 1 : 0;
                        
                        const pathData = [
                          `M ${x1} ${y1}`,
                          `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
                          `L ${x3} ${y3}`,
                          `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}`,
                          'Z'
                        ].join(' ');
                        
                        const colors = {
                          blue: '#3B82F6',
                          cyan: '#06B6D4',
                          orange: '#F97316',
                          green: '#10B981'
                        };
                        
                        return (
                          <path
                            key={index}
                            d={pathData}
                            fill={colors[hospital.color] || colors.blue}
                            className="hover:opacity-80 transition-opacity"
                          />
                        );
                      });
                    })()
                  ) : (
                    <circle cx="50" cy="50" r="40" fill="#E5E7EB" />
                  )}
                </svg>
                
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-3xl font-bold text-gray-900">{stats.total_cases}</p>
                  <p className="text-xs text-gray-500 uppercase">Total Cases</p>
                </div>
              </div>
            </div>

            {/* Legend with Real Data */}
            <div className="space-y-3">
              {hospitalPercentages.length > 0 ? (
                hospitalPercentages.map((hospital, index) => {
                  const colors = hospitalColors[hospital.color] || hospitalColors.blue;
                  return (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 ${colors.bg} rounded-full`}></div>
                        <span className="text-sm text-gray-700">{hospital.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{hospital.percentage}%</span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4 text-gray-400">
                  <p>No hospital data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Months */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Top Performing Months</h2>
            {topMonths.length > 0 ? (
              <div className="space-y-4">
                {topMonths.map((month, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-xs font-medium text-gray-500 uppercase">{month.month}</p>
                        <p className="text-2xl font-bold text-blue-600">{month.date}</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gray-900">${month.revenue.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">{month.cases} total cases</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold ${month.positive ? 'text-green-600' : 'text-red-600'}`}>
                        {month.change}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>No data available</p>
              </div>
            )}
          </div>

          {/* Hospital Performance */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Hospital Performance</h2>
              <Link to="/hospital-earnings" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                View All
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cases</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billed Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {hospitalData.length > 0 ? (
                    hospitalData.map((hospital, index) => {
                      const colors = hospitalColors[hospital.color] || hospitalColors.blue;
                      return (
                        <tr key={index} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => window.location.href = '/hospital-earnings'}>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`${colors.light} rounded-lg p-2`}>
                                <div className={colors.text}>
                                  <Icons.Building />
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-900">{hospital.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">{hospital.cases}</td>
                          <td className="px-4 py-4 text-sm font-semibold text-gray-900">${hospital.amount.toLocaleString()}</td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              hospital.status === 'HEALTHY' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {hospital.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-400">
                        No hospital data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Reports;
